import React from "react";
import { defineMessages, useIntl } from "react-intl"
import { useValue } from "./lib/db/react";
import { MenuOutlined } from "@ant-design/icons";
import { Button, Drawer } from "antd";
import { usePushError } from "./api";
import { UiContext } from "./contexts/ui";
import { cacheStorage, dbStorage, db } from "./db/state";
import { Stream } from "./lib";
import Errors from "./shared/Errors";
import StoreError from "./shared/StoreError";
import Background from "./components/Background";
import Widgets from "./components/Widgets";
import 'antd/dist/reset.css';
import "./Dashboard.sass";
import Settings from "./components/Settings";

const messages = defineMessages({
  pageTitle: {
    id: "app.pageTitle",
    description: "Page title that Tabliss displays in the title bar.",
    defaultMessage: "New Tab",
  },
});

const Root: React.FC = () => {
  // Set page title
  const intl = useIntl();
  React.useEffect(() => {
    document.title = intl.formatMessage(messages.pageTitle);
  }, [intl]);

  // Wait for storage to be ready before displaying
  const [ready, setReady] = React.useState(false);
  const [error, setError] = React.useState(false);

  const pushError = usePushError();
  const handleError =
    (message: string, showError: boolean) => (error: Error) => {
      pushError({ message });
      console.error(error);
      console.error("Caused by:", error.cause);
      if (showError) setError(true);
    };

  React.useEffect(() => {
    const subscriptions = Promise.all([
      // Config database
      dbStorage
        .then((errors) =>
          Stream.subscribe(
            errors,
            handleError(
              "Cannot save your settings. You may have hit the maximum storage capacity.",
              true,
            ),
          ),
        )
        .catch(
          handleError(
            "Cannot open settings storage. Your settings cannot be loaded or saved.",
            true,
          ),
        ),
      // Cache database
      cacheStorage
        .then((errors) =>
          Stream.subscribe(
            errors,
            handleError(
              "Cannot save cache. Start up performance may be degraded.",
              false,
            ),
          ),
        )
        .catch(
          handleError(
            "Cannot open cache. Start up performance may be degraded.",
            false,
          ),
        ),
    ]);

    // Storage is ready
    subscriptions.then(() => {
      setReady(true);
    });

    return () => {
      // Remove error subscriptions
      subscriptions.then(([dbSub, cacheSub]) => {
        if (dbSub) dbSub();
        if (cacheSub) cacheSub();
      });
    };
  }, []);

  const { errors, settings, toggleSettings, toggleErrors } = React.useContext(UiContext);

  const background = useValue(db, "background");
  const theme = (background.display.luminosity ?? 0) > 0 ? "light" : "dark";

  return (
    <>
      {ready ? <div className={`Dashboard fullscreen ${theme}`}>
      <Background />
      <Widgets />
      <div className="DashboardSetting">
        <Button size="small" icon={<MenuOutlined />} onClick={toggleSettings} />
      </div>
      <Drawer
        rootStyle={{ padding: 10 }}
        style={{ width: window.innerWidth < 400 ? '80vw' : '' }}
        closable={false}
        placement="left"
        open={settings}
        onClose={toggleSettings}
      >
        {settings ? <Settings /> : null}
      </Drawer>
    </div>: null}
      {errors ? <Errors onClose={toggleErrors} /> : null}
      {error ? <StoreError onClose={() => setError(false)} /> : null}
    </>
  );
};

export default Root;
